import { ApiError, type StaffRole, type StaffUser } from '@central/api';
import { Card, PageHeader, StatusChip, color, formatAgo, shownError, useReauth } from '@central/ui';
import {
  Alert,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { TrustedBrowsers, YourSignIn } from '../components/AccountSecurity';
import { NotificationPrefs } from '../components/NotificationPrefs';
import { StaffSsoProviders } from '../components/StaffSsoProviders';
import { DataTable, Empty, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useMe } from '../lib/me';

const roles: Array<{ value: StaffRole; label: string }> = [
  { value: 'Owner', label: 'Owner' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Support', label: 'Support' },
  { value: 'Reseller', label: 'Reseller' },
];

const roleHelp: Record<StaffRole, string> = {
  Owner: 'everything, plus staff and access',
  Admin: 'edits customers, licenses and environments',
  Support: 'reads them',
  Reseller:
    'a partner’s engineer: tier 1 support and installs for its reseller’s customers, no licensing',
};

/** The resellers, for the reseller picker; loaded only while one is needed. */
function useResellerOptions(enabled: boolean) {
  const api = useApi();
  const q = useQuery({
    queryKey: keys.resellers,
    queryFn: () => api.resellers.list(),
    enabled,
    staleTime: 60_000,
  });
  return {
    data: (q.data?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
    names: new Map((q.data?.items ?? []).map((r) => [r.id, r.name])),
    isPending: enabled && q.isPending,
  };
}

/** "Passkey ×2 · TOTP", or none. */
function factorsText(u: StaffUser): string | undefined {
  const f = u.secondFactors;
  const parts = [
    f.passkeys > 0 ? (f.passkeys === 1 ? 'Passkey' : `Passkey ×${f.passkeys}`) : '',
    f.totp ? 'TOTP' : '',
    u.ssoLinks.length > 0 ? 'SSO' : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

/**
 * Staff & access: the signed-in user's own sign-in, trusted browsers and
 * email notifications, and for Owners and Admins the staff list. Owners
 * invite, change roles, disable, and issue setup links (password reset) and
 * second-factor recovery links.
 */
export function Staff() {
  const me = useMe();
  const role = me.user.role;
  const [inviting, setInviting] = useState(false);
  return (
    <>
      <PageHeader
        title={role === 'Reseller' ? 'Your account' : 'Staff & access'}
        subtitle="Staff sign in with a password and a passkey or authenticator app."
        actions={
          role === 'Owner' && (
            <Button color="dark" size="sm" h={40} onClick={() => setInviting(true)}>
              Invite staff
            </Button>
          )
        }
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <YourSignIn me={me} />
        </div>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <TrustedBrowsers />
        </div>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <NotificationPrefs />
        </div>
      </div>
      {(role === 'Owner' || role === 'Admin') && <StaffList canManage={role === 'Owner'} />}
      {(role === 'Owner' || role === 'Admin') && <StaffSsoProviders resellerId="" />}
      <Invite opened={inviting} onClose={() => setInviting(false)} />
    </>
  );
}

function StaffList({ canManage }: { canManage: boolean }) {
  const api = useApi();
  const me = useMe();
  const list = useQuery({ queryKey: keys.staff, queryFn: () => api.staff.list() });
  const [managing, setManaging] = useState<string>();
  const items = list.data?.items ?? [];
  const resellers = useResellerOptions(items.some((u) => u.resellerId));
  const target = items.find((u) => u.id === managing);

  return (
    <Card title={`Staff · ${items.length}`} padded={false}>
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No staff yet.</Empty>
          ) : (
            <DataTable
              label="Staff"
              minWidth={760}
              head={['Name', 'Role', 'Second factor', 'Last sign-in', 'Status', '']}
            >
              {items.map((u) => {
                const factors = factorsText(u);
                return (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {u.id === me.user.id && <span style={muted}> (you)</span>}
                      </div>
                      <div style={{ fontSize: 13, ...muted }}>{u.email}</div>
                    </Table.Td>
                    <Table.Td>
                      {u.role}
                      {u.resellerId && (
                        <div style={{ fontSize: 13, ...muted }}>
                          {resellers.names.get(u.resellerId) ?? 'Reseller'}
                          {u.resellerAdmin && ' · admin'}
                        </div>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {factors ?? <StatusChip tone="warn">Pending setup</StatusChip>}
                    </Table.Td>
                    <Table.Td style={muted}>
                      {u.lastSignInAt ? formatAgo(u.lastSignInAt) : 'Never'}
                    </Table.Td>
                    <Table.Td>
                      {u.enabled ? (
                        <StatusChip tone="ok">Enabled</StatusChip>
                      ) : (
                        <StatusChip tone="unk">Disabled</StatusChip>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {canManage && (
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          aria-label={`Manage ${u.name}`}
                          onClick={() => setManaging(u.id)}
                        >
                          Manage
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </DataTable>
          )
        }
      </QueryState>
      <div
        style={{
          padding: '12px 20px',
          fontSize: 13,
          color: color.textMuted,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 24px',
        }}
      >
        {roles.map((r) => (
          <span key={r.value}>
            <b style={{ color: color.text }}>{r.label}</b>: {roleHelp[r.value]}
          </span>
        ))}
      </div>
      {target && (
        <Manage
          key={target.id}
          user={target}
          self={target.id === me.user.id}
          onClose={() => setManaging(undefined)}
        />
      )}
    </Card>
  );
}

/** One issued link: what it is, who it is for, and whether it was emailed. */
export interface IssuedLink {
  url: string;
  what: string;
  email: string;
  emailed: boolean;
}

/**
 * A setup URL, shown once with a copy button: the fallback when its email
 * is slow or lost, and the only way to pass it on while email is off.
 */
export function OneTimeLink({ url, what, email, emailed }: IssuedLink) {
  return (
    <Stack gap={8}>
      <Text size="sm">
        {emailed
          ? `We emailed this ${what} to ${email}. You can also copy it and send it yourself.`
          : `Email is off, so send this ${what} to ${email} yourself.`}{' '}
        It is shown only once, works once, and expires in 24 hours.
      </Text>
      <Code
        block
        style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
        data-testid="setup-url"
      >
        {url}
      </Code>
      <CopyButton value={url}>
        {({ copied, copy }) => (
          <Button size="xs" variant="default" onClick={copy} style={{ alignSelf: 'flex-start' }}>
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        )}
      </CopyButton>
    </Stack>
  );
}

/**
 * Invite a staff user (Owner). `reseller` starts the form on the Reseller
 * role with that reseller chosen, as a reseller's page opens it.
 */
export function Invite({
  opened,
  onClose,
  reseller,
}: {
  opened: boolean;
  onClose: () => void;
  reseller?: string;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const initialRole: StaffRole = reseller ? 'Reseller' : 'Support';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRole>(initialRole);
  const [resellerId, setResellerId] = useState(reseller ?? '');
  const [resellerAdmin, setResellerAdmin] = useState(false);
  const resellers = useResellerOptions(opened && role === 'Reseller');
  const [link, setLink] = useState<IssuedLink>();
  const create = useMutation({
    mutationFn: () =>
      api.staff.create({
        email: email.trim(),
        name: name.trim(),
        role,
        resellerId: role === 'Reseller' ? resellerId : undefined,
        resellerAdmin: role === 'Reseller' && resellerAdmin ? true : undefined,
      }),
    onSuccess: async (r) => {
      setLink({ url: r.setupUrl, what: 'setup link', email: r.user.email, emailed: r.emailed });
      await qc.invalidateQueries({ queryKey: keys.staff });
    },
  });
  const fields = create.error instanceof ApiError ? create.error.fields : undefined;

  const close = () => {
    setEmail('');
    setName('');
    setRole(initialRole);
    setResellerId(reseller ?? '');
    setResellerAdmin(false);
    setLink(undefined);
    create.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <Modal opened={opened} onClose={close} title="Invite staff">
      {link ? (
        <Stack gap={14}>
          <OneTimeLink {...link} />
          <Group justify="flex-end">
            <Button color="dark" onClick={close}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <form onSubmit={submit} noValidate>
          <Stack gap={14}>
            <TextInput
              label="Work email"
              type="email"
              data-autofocus
              value={email}
              error={fields?.email && 'Enter a valid email address.'}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Name"
              value={name}
              error={fields?.name && 'Enter their name.'}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <Select
              label="Role"
              data={roles}
              value={role}
              allowDeselect={false}
              onChange={(v) => v && setRole(v as StaffRole)}
            />
            {role === 'Reseller' && (
              <Select
                label="Reseller"
                description="They see only this reseller’s customers."
                placeholder={resellers.isPending ? 'Loading…' : 'Choose a reseller'}
                data={resellers.data}
                value={resellerId || null}
                onChange={(v) => setResellerId(v ?? '')}
                error={fields?.resellerId && 'Choose the reseller they work for.'}
                nothingFoundMessage="Add a reseller on the Resellers page first"
              />
            )}
            {role === 'Reseller' && (
              <Checkbox
                label="Reseller admin"
                description="Invites and manages the reseller’s own team."
                checked={resellerAdmin}
                onChange={(e) => setResellerAdmin(e.currentTarget.checked)}
              />
            )}
            {create.error && !fields && (
              <Alert color="red">
                {errorMessage(create.error, {
                  duplicate: 'A staff account with that email already exists.',
                })}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" color="dark" loading={create.isPending}>
                Create and get link
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}

/**
 * The identity providers that sign this user in, each with Unlink: the
 * identity then no longer signs them in, and their next SSO sign-in starts
 * linking again.
 */
function SsoLinks({ user }: { user: StaffUser }) {
  const api = useApi();
  const qc = useQueryClient();
  const providers = useQuery({
    queryKey: keys.staffSsoEvery,
    queryFn: () => api.staffSso.list(),
    staleTime: 60_000,
  });
  const names = new Map((providers.data?.items ?? []).map((p) => [p.id, p.name]));
  const unlink = useMutation({
    mutationFn: (providerId: string) => api.staff.unlinkSso(user.id, providerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.staff }),
  });
  return (
    <Stack gap={6}>
      <Text size="sm" fw={600}>
        Single sign-on
      </Text>
      {user.ssoLinks.map((l) => {
        const name = names.get(l.providerId) ?? 'Identity provider';
        return (
          <Group key={l.providerId} justify="space-between" gap={8}>
            <Text size="sm">{name}</Text>
            <Button
              variant="default"
              size="xs"
              loading={unlink.isPending && unlink.variables === l.providerId}
              onClick={() => unlink.mutate(l.providerId)}
            >
              Unlink <VisuallyHidden>{name}</VisuallyHidden>
            </Button>
          </Group>
        );
      })}
      {unlink.error ? <Alert color="red">{errorMessage(unlink.error)}</Alert> : null}
    </Stack>
  );
}

function Manage({ user, self, onClose }: { user: StaffUser; self: boolean; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [role, setRole] = useState<StaffRole>(user.role);
  const [resellerId, setResellerId] = useState(user.resellerId ?? '');
  const resellers = useResellerOptions(role === 'Reseller');
  const [link, setLink] = useState<IssuedLink>();
  const saved = () => qc.invalidateQueries({ queryKey: keys.staff });
  // A recovery link needs this session's second factor proved in the last 10 minutes.
  const { guard, prompt } = useReauth(useMe().user.secondFactors, {
    api: api.auth,
    describeError: errorMessage,
    buttonColor: 'dark',
  });

  const update = useMutation({
    mutationFn: (b: {
      role?: StaffRole;
      resellerId?: string;
      enabled?: boolean;
      resellerAdmin?: boolean;
    }) => api.staff.update(user.id, b),
    onSuccess: saved,
  });
  const reset = useMutation({
    mutationFn: () => api.staff.setupLink(user.id),
    onSuccess: (r) =>
      setLink({
        url: r.setupUrl,
        what: 'password reset link',
        email: user.email,
        emailed: r.emailed,
      }),
  });
  const recover = useMutation({
    mutationFn: () => guard(() => api.staff.recoveryLink(user.id)),
    onSuccess: (r) =>
      setLink({ url: r.setupUrl, what: 'recovery link', email: user.email, emailed: r.emailed }),
  });
  const error = shownError(update.error ?? reset.error ?? recover.error);
  const busy = update.isPending || reset.isPending || recover.isPending;

  return (
    <>
      <Modal opened onClose={onClose} title={`${user.name}${self ? ' (you)' : ''}`}>
        {link ? (
          <Stack gap={14}>
            <OneTimeLink {...link} />
            <Group justify="flex-end">
              <Button color="dark" onClick={onClose}>
                Done
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap={16}>
            <Text size="sm" style={muted}>
              {user.email}
            </Text>
            <Group align="flex-end" gap={8}>
              <Select
                label="Role"
                data={roles}
                value={role}
                allowDeselect={false}
                onChange={(v) => v && setRole(v as StaffRole)}
                style={{ flex: 1 }}
              />
              <Button
                color="dark"
                disabled={
                  (role === user.role && resellerId === (user.resellerId ?? '')) ||
                  (role === 'Reseller' && !resellerId) ||
                  busy
                }
                loading={update.isPending && update.variables?.role !== undefined}
                onClick={() => update.mutate(role === 'Reseller' ? { role, resellerId } : { role })}
              >
                Save role
              </Button>
            </Group>
            {role === 'Reseller' && (
              <Select
                label="Reseller"
                placeholder={resellers.isPending ? 'Loading…' : 'Choose a reseller'}
                data={resellers.data}
                value={resellerId || null}
                onChange={(v) => setResellerId(v ?? '')}
              />
            )}
            {user.role === 'Reseller' && (
              <Checkbox
                label="Reseller admin"
                description="Invites and manages the reseller’s own team."
                checked={user.resellerAdmin}
                disabled={busy}
                onChange={(e) => update.mutate({ resellerAdmin: e.currentTarget.checked })}
              />
            )}
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Sign-in help
              </Text>
              <Group gap={8}>
                <Button
                  variant="default"
                  size="sm"
                  disabled={!user.enabled || busy}
                  loading={reset.isPending}
                  onClick={() => reset.mutate()}
                >
                  Password reset link
                </Button>
                {!self && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={!user.enabled || busy}
                    loading={recover.isPending}
                    onClick={() => recover.mutate()}
                  >
                    Second-factor recovery link
                  </Button>
                )}
              </Group>
              <Text size="xs" style={muted}>
                A password reset keeps their passkeys and authenticator app. A recovery link also
                removes them, so they set up a new one. Either link replaces any unused one.
              </Text>
            </Stack>
            {user.ssoLinks.length > 0 && <SsoLinks user={user} />}
            {error ? <Alert color="red">{errorMessage(error)}</Alert> : null}
            <Group justify="space-between">
              <Button
                variant="subtle"
                color={user.enabled ? 'red' : 'dark'}
                disabled={busy}
                loading={update.isPending && update.variables?.enabled !== undefined}
                onClick={() => update.mutate({ enabled: !user.enabled })}
              >
                {user.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      {prompt}
    </>
  );
}
